import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  ChatThread,
  ChatMessage,
  ContentRun,
} from "../../../shared/types";

// ── Response shapes ──

interface ThreadResponse {
  data: ChatThread & { messages: ChatMessage[] };
}

interface CreateThreadPayload {
  run_id: string;
  idea_index: number;
}

interface SendMessagePayload {
  content: string;
}

interface FinalizeResponse {
  data: ContentRun;
}

// ── Queries ──

export function useChatThread(threadId: string | undefined) {
  return useQuery({
    queryKey: ["chat-threads", threadId],
    queryFn: () =>
      api.get<ThreadResponse>(`/api/chat/threads/${threadId}`),
    enabled: !!threadId,
    // Poll every 2s when last message is from user (waiting for AI response)
    refetchInterval: (query) => {
      const thread = query.state.data?.data;
      if (!thread) return false;
      const messages = thread.messages;
      if (messages.length === 0) return false;
      const lastMessage = messages[messages.length - 1];
      return lastMessage?.role === "user" ? 2_000 : false;
    },
  });
}

// ── Mutations ──

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateThreadPayload) =>
      api.post<ThreadResponse>("/api/chat/threads", payload),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
    },
  });
}

export function useSendMessage(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendMessagePayload) =>
      api.post<{ data: ChatMessage }>(
        `/api/chat/threads/${threadId}/messages`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-threads", threadId] });
    },
  });
}

export function useFinalizeThread(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<FinalizeResponse>(
        `/api/chat/threads/${threadId}/finalize`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-threads", threadId] });
      qc.invalidateQueries({ queryKey: ["content-runs"] });
    },
  });
}
